import { PhoneIcon } from '@heroicons/react/24/outline'
import { ReactElement } from 'react'
import { ActivityDrawer } from 'app/components/activities/ActivityDrawer'
import { StatsCards } from 'app/components/analytics/StatsCards'
import { SectionCard } from 'app/components/SectionCard'
import { ContactListModal } from 'app/components/contact-list-modal/ContactListModal'
import { ContactDrawer } from 'app/components/contacts/ContactDrawer'
import SimpleTable from 'app/components/table/SimpleTable'
import TableLayout from 'app/components/table/TableLayout'
import { TableCountFooter } from 'app/components/table/TableCountFooter'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import { AI_AGENT_COLUMN_WEIGHTS } from 'app/table-columns/activities'
import { ActivityJson } from 'app/types/entities'
import { CallTimeHeatmap } from './CallTimeHeatmap'
import { NewPatientCallsGraph } from './NewPatientCallsGraph'
import { defaultExcludeFilters } from './useAiAgentVoice'
import type { UseAiAgentVoiceReturn } from './useAiAgentVoice'

export type AiAgentVoiceComponentProps = UseAiAgentVoiceReturn

function StatsCardsSkeleton(): ReactElement {
  return (
    <div className="flex gap-4 p-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex-1 animate-pulse rounded-lg border border-default bg-surface p-4">
          <div className="mb-3 h-3 w-2/3 rounded bg-surface-strong" />
          <div className="h-7 w-1/2 rounded bg-surface-strong" />
        </div>
      ))}
    </div>
  )
}

function ActivityTableSkeleton(): ReactElement {
  return (
    <div className="animate-pulse space-y-2 p-4">
      <div className="flex gap-4 border-b border-default pb-2">
        {[`w-24`, `w-32`, `w-40`, `w-28`, `w-20`].map((w, i) => (
          <div key={i} className={`h-3 rounded bg-surface-strong ${w}`} />
        ))}
      </div>
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex gap-4 py-2">
          {[`w-24`, `w-32`, `w-40`, `w-28`, `w-20`].map((w, j) => (
            <div key={j} className={`h-3 rounded bg-surface-muted ${w}`} />
          ))}
        </div>
      ))}
    </div>
  )
}

export function AiAgentVoiceComponent(props: AiAgentVoiceComponentProps): ReactElement {
  const {
    activitiesState,
    metricsData,
    npCallActivities,
    isNpCallsLoading,
    dateRange,
    stats,
    columns,
    filterableColumns,
    focusedActivity,
    openDrawer,
    isActivityLoading,
    focusedContact,
    openContactDrawer,
    metricFilter,
    metricModalTitle,
    modalActivity,
    modalActivityDrawerOpen,
    modalActivityContact,
    modalActivityContactDrawerOpen,
    modalContacts,
    modalContactsLoading,
    modalAppointments,
    appointmentModalColumns,
    modal,
    filteredNpCallActivities,
    npFilterValue,
    modalActivityColumns,
    contactModalColumns,
    contactModalFilterableColumns,
    loadActivity,
    activityHandler,
    closeDrawerHandler,
    handleContactClick,
    closeContactDrawer,
    closeMetricModal,
    closeModalActivityDrawer,
    handleModalActivityContactClick,
    closeModalActivityContactDrawer,
    setNpSearchValue,
    setNpFilterValue,
  } = props

  // Size the call-list well to its content, between a comfortable minimum (so a
  // handful of rows still get some room) and a max ~30% taller than the old
  // fixed 800px. ~128px of chrome (toolbar + header + padding) + ~48px per row.
  const npRowCount = activitiesState?.hits?.length ?? 0
  const npWellHeight = Math.min(1040, Math.max(500, 128 + npRowCount * 48))

  return (
    <>
      <div className="[&>div]:my-0">{metricsData ? <StatsCards stats={stats} /> : <StatsCardsSkeleton />}</div>
      <div className="mt-6">
        <NewPatientCallsGraph
          activities={npCallActivities}
          fromDate={dateRange.fromDate}
          toDate={dateRange.toDate}
          total={metricsData?.newPatientCalls}
          isLoading={isNpCallsLoading}
        />
        <CallTimeHeatmap activities={activitiesState?.hits ?? []} isLoading={!activitiesState} />
      </div>
      <SectionCard attached title="New Patient Call List" className="mb-6" contentClassName="bg-surface p-0">
        {!activitiesState ? (
          <div className="flex h-[500px] flex-col overflow-hidden p-4">
            <ActivityTableSkeleton />
          </div>
        ) : (
          <div className="relative flex flex-col overflow-hidden p-4" style={{ height: npWellHeight }}>
            <TableLayout
              data={activitiesState}
              columns={columns as any}
              filterableColumns={filterableColumns}
              initialFilters={defaultExcludeFilters}
              defaultColumnWeights={AI_AGENT_COLUMN_WEIGHTS}
              meta={{
                getRowStyle: (row) => ((row as ActivityJson)?.id === focusedActivity?.id ? `table-selected-row` : ``),
              }}
              emptyState={
                <TableEmptyState
                  icon={<PhoneIcon className="h-6 w-6" />}
                  title="No new patient calls yet"
                  description="New patient calls in this date range will show up here."
                />
              }
            />
            <TableCountFooter shown={activitiesState.hits.length} total={activitiesState.total} noun="call" />
          </div>
        )}
      </SectionCard>
      <ActivityDrawer
        activity={focusedActivity}
        isLoading={isActivityLoading}
        open={openDrawer}
        onChange={loadActivity}
        onClose={closeDrawerHandler}
        onContactClick={handleContactClick}
      />
      <ContactDrawer
        contact={focusedContact}
        openDrawer={openContactDrawer}
        onClose={closeContactDrawer}
        onChange={() => {}}
        highlightActivityId={focusedActivity?.id}
      />

      <ContactListModal.Root
        open={!!metricFilter}
        onClose={closeMetricModal}
        contacts={modal.contacts}
        selectedContact={modal.selectedContact}
        drawerOpen={modal.drawerOpen}
        isNavigating={modal.isNavigating}
        onContactClick={modal.handleContactClick}
        onCloseDrawer={modal.closeDrawer}
        onContactChange={modal.setSelectedContact}
        nextContact={modal.nextContact}
        prevContact={modal.prevContact}
      >
        {metricFilter === `newPatientCalls` ? (
          <>
            <div className="flex h-full w-full flex-col overflow-hidden">
              <ContactListModal.Header
                title={metricModalTitle}
                count={filteredNpCallActivities.length}
                onClose={closeMetricModal}
              />
              <div className="flex h-full w-full flex-col overflow-hidden">
                <SimpleTable
                  data={filteredNpCallActivities}
                  columns={modalActivityColumns as any}
                  filterableColumns={filterableColumns}
                  onSearchValueChange={setNpSearchValue}
                  onFiltersChange={setNpFilterValue}
                  enableDeepLinking={false}
                  initialFilters={defaultExcludeFilters}
                  meta={{
                    getRowStyle: (row) => ((row as ActivityJson)?.id === modalActivity?.id ? `table-selected-row` : ``),
                  }}
                  hideFooter
                />
              </div>
            </div>
            <>
              <ActivityDrawer
                activity={modalActivity}
                open={modalActivityDrawerOpen}
                onClose={closeModalActivityDrawer}
                onContactClick={handleModalActivityContactClick}
              />
              <ContactDrawer
                contact={modalActivityContact}
                openDrawer={modalActivityContactDrawerOpen}
                onClose={closeModalActivityContactDrawer}
                onChange={() => {}}
              />
            </>
          </>
        ) : (
          <>
            <div className="flex h-full w-full flex-col overflow-hidden">
              <ContactListModal.Header
                title={metricModalTitle}
                count={modalAppointments.length}
                onClose={closeMetricModal}
              />
              {modalContactsLoading ? (
                <div className="flex flex-1 items-center justify-center">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-strong border-t-(--fg-default)" />
                </div>
              ) : (
                <div className="flex h-full w-full flex-col overflow-hidden">
                  <SimpleTable
                    data={modalAppointments}
                    columns={appointmentModalColumns as any}
                    enableDeepLinking={false}
                    hideFooter
                  />
                </div>
              )}
            </div>
            <ContactDrawer
              contact={modalActivityContact}
              openDrawer={modalActivityContactDrawerOpen}
              onClose={closeModalActivityContactDrawer}
              onChange={() => {}}
            />
          </>
        )}
      </ContactListModal.Root>
    </>
  )
}
